import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Snowflake } from 'lucide-react';
import api from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';
import PageHeading from '../../components/PageHeading';

const DEFAULT_COMPANY = 'company-001';

export default function ChillerDashboard() {
  const user = useAuthStore((s) => s.user);
  const companyId = user?.company_id || user?.companyId || DEFAULT_COMPANY;

  const { data: status } = useQuery({
    queryKey: ['chiller', 'status', companyId],
    queryFn: async () =>
      (await api.get('/chiller/status', { params: { companyId } })).data?.data,
  });

  const enabled = !!status?.chiller_module_enabled;

  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: ['inspection-modules', 'CHILLER', 'buildings'],
    queryFn: async () =>
      (await api.get('/inspection-modules/CHILLER/buildings')).data?.data || [],
    enabled,
  });

  const buildings = Array.isArray(data) ? data : [];

  return (
    <div>
      <PageHeading
        title="Chiller"
        infoTitle="Chiller inspections"
        infoChildren={
          <p className="text-sm">
            Buildings that need this inspection. Open a row to work on that building. This list is the
            home for the module while it is turned on.
          </p>
        }
      />

      {!enabled && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
          Chiller module is off.{' '}
          <Link to="/settings/inspection" className="font-semibold underline">
            Turn it on in Inspection module
          </Link>
          .
        </div>
      )}

      {enabled && (
        <div className="rounded-xl border bg-white overflow-hidden">
          {isLoading && <p className="px-4 py-8 text-gray-500 text-sm">Loading buildings…</p>}
          {isError && (
            <p className="px-4 py-8 text-red-600 text-sm">
              {error?.response?.data?.error || 'Failed to load buildings'}
            </p>
          )}
          {!isLoading && !isError && (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Building</th>
                  <th className="px-4 py-3">Start month</th>
                  <th className="px-4 py-3">Method</th>
                  <th className="px-4 py-3">Next inspection</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {buildings.map((b) => (
                  <tr key={b.id} className="hover:bg-blue-50/40">
                    <td className="px-4 py-3">
                      <Link to={`/buildings/${b.id}/chiller`} className="font-semibold text-primary-700 hover:underline">
                        {b.name || b.id}
                      </Link>
                      <p className="text-xs text-gray-500">{b.address || '—'}</p>
                    </td>
                    <td className="px-4 py-3 tabular-nums">{b.cycle_start_month}</td>
                    <td className="px-4 py-3">{b.frequency}</td>
                    <td className="px-4 py-3 tabular-nums">{b.next_inspection_month || '—'}</td>
                  </tr>
                ))}
                {!buildings.length && (
                  <tr>
                    <td colSpan={4} className="px-4 py-10 text-center text-gray-500">
                      <Snowflake className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      No buildings need this inspection yet. Tick “This building has chiller / plant” on Edit Building.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
